/**
 * Thin fetch wrapper around the VendFlow API.
 *
 * Deliberately not a generated client: the surface is small enough that a
 * hand-written wrapper stays readable, and it lets 401 handling live in exactly
 * one place — a session that has idled out redirects to sign-in rather than
 * surfacing an opaque error on whatever screen the user was on.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public payload?: unknown,
  ) {
    super(message);
  }
}

/**
 * What to tell the user about a caught error. The API client only ever throws
 * Errors (ApiError carries the server's wording), so this is their message.
 */
export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

let onUnauthorised: (() => void) | null = null;
export function setUnauthorisedHandler(fn: () => void) {
  onUnauthorised = fn;
}

const TOKEN_KEY = 'vf_token';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

async function request<T>(
  path: string,
  init: RequestInit & { raw?: boolean } = {},
): Promise<T> {
  const token = tokenStore.get();
  const headers = new Headers(init.headers);

  // FormData sets its own boundary — never override it.
  if (!(init.body instanceof FormData) && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers, credentials: 'include' });

  if (res.status === 401) {
    tokenStore.clear();
    onUnauthorised?.();
    throw new ApiError(401, 'Your session has expired. Please sign in again.', 'SESSION_EXPIRED');
  }

  if (init.raw) return res as unknown as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return (await res.text()) as unknown as T;
  }

  const body = await res.json();
  if (!res.ok) {
    // The server refuses everything until a forced password change (or an
    // Admin's two-factor setup) is done; signing in again shows that step
    // rather than a screen of 403s.
    if (body.code === 'PASSWORD_CHANGE_REQUIRED' || body.code === 'TWO_FACTOR_SETUP_REQUIRED') {
      tokenStore.clear();
      onUnauthorised?.();
    }
    throw new ApiError(res.status, body.error ?? 'Request failed', body.code, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) => {
    const qs = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return request<T>(`${path}${qs}`);
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  // A body on DELETE is unusual but the destructive endpoints require an
  // explicit `{ confirm: true }`, so the intent is carried in the request
  // itself rather than inferred from the fact that it was sent.
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),
  /** Fetches an authenticated file endpoint and returns an object URL for it. */
  blobUrl: async (path: string, params?: Record<string, unknown>) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    const res = await request<Response>(`${path}${qs}`, { raw: true });
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /** Triggers a browser download for CSV / PDF endpoints. */
  download: async (path: string, filename: string, params?: Record<string, unknown>) => {
    const url = await api.blobUrl(path, params);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ─── Formatting ──────────────────────────────────────────────────────────────

export function money(value: unknown, currency = 'GBP'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(n);
}

export function number(value: unknown, dp = 0): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function percent(value: unknown, dp = 1): string {
  if (value === null || value === undefined || value === '') return '—';
  return `${Number(value).toFixed(dp)}%`;
}

/**
 * Percentages are stored as fractions — 30% is 0.3.
 *
 * That is what the pricing engine wants, since every calculation is
 * `price × (1 − discount)`, and it is what the quote builder has always sent.
 * Nobody types a fraction, though, so the conversion belongs at the edge of
 * the form: these two functions are that edge, and every percentage field
 * goes through them rather than doing its own arithmetic inline.
 *
 * Rounding is explicit because binary floating point is not: 0.1 × 100 is
 * 10.000000000000002, which is not a number anyone wants to see in a box.
 */

/** Stored fraction → the number shown in a percentage box. `0.3` → `'30'`. */
export function toPercentInput(fraction: unknown): string {
  if (fraction === null || fraction === undefined || fraction === '') return '';
  const n = Number(fraction);
  if (Number.isNaN(n)) return '';
  return String(Number((n * 100).toFixed(4)));
}

/** A typed percentage → the fraction the API stores. `'30'` → `'0.3'`. */
export function fromPercentInput(typed: unknown): string | null {
  if (typed === null || typed === undefined || String(typed).trim() === '') return null;
  const n = Number(typed);
  if (Number.isNaN(n)) return null;
  return String(Number((n / 100).toFixed(6)));
}

export function date(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function timeOnly(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Values for `<input type="date">` and `<input type="time">`.
 *
 * Both controls speak wall-clock time in the user's own zone, and that is also
 * how the browser parses `new Date('YYYY-MM-DDTHH:mm')` on the way back — so a
 * time box must be filled from the local reading of the stored instant, never
 * from `toISOString()`. Getting that wrong is silent and cumulative: the box
 * shows an hour early, and saving it writes an hour earlier still.
 */
export function localDayKey(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function localTimeValue(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "3 days ago" / "in 5 days" — used on activity feeds and countdowns. */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000_000], ['month', 2_592_000_000], ['day', 86_400_000],
    ['hour', 3_600_000], ['minute', 60_000],
  ];
  for (const [unit, ms] of units) {
    if (abs >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return 'just now';
}

/**
 * Words that are initialisms, not words, and must stay in capitals.
 *
 * Sentence-casing an enum value is right for `IN_REPROCESSING` and wrong for
 * `IN_QC` — "In qc" is not a thing anyone says, and it appeared on status
 * badges, filter dropdowns, machine timelines and the file library, because
 * every one of them goes through `humanise`. Fixing the callers one at a time
 * would leave the next enum value that contains QC broken again.
 */
const INITIALISMS = new Set(['QC', 'PDI', 'VAT', 'PO', 'POD', 'RAMS', 'PDF', 'CSV', 'ID', 'VIN', 'UK']);

/** ENUM_VALUE → "Enum value" for display, keeping initialisms capitalised. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';

  const words = value.replace(/_/g, ' ').toLowerCase().split(' ');
  const cased = words.map((word, i) => {
    if (INITIALISMS.has(word.toUpperCase())) return word.toUpperCase();
    // Only the first word is capitalised — this is sentence case, not title
    // case, and "In Reprocessing" would be a different (wrong) house style.
    return i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
  });

  return cased.join(' ');
}
